import React from "react";
import { Ellipsis, PageButton, PaginationWrap, TotalFound } from "./styles";

type PaginationProps = {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
};

type PageItem = number | "ellipsis-left" | "ellipsis-right";

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  total,
  onPageChange,
  disabled = false
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);
  const safeTotal = Math.max(0, total || 0);
  const pageItems = getPageItems(safePage, safeTotalPages);

  const isFirstPage = safePage === 1;
  const isLastPage = safePage === safeTotalPages;
  const shouldShowPageControls = safeTotalPages > 1;

  return (
    <PaginationWrap>
      {shouldShowPageControls && (
        <>
          <PageButton
            type="button"
            aria-label="Go to first page"
            onClick={() => onPageChange(1)}
            disabled={disabled || isFirstPage}
          >
            {"<<"}
          </PageButton>

          <PageButton
            type="button"
            aria-label="Go to previous page"
            onClick={() => onPageChange(safePage - 1)}
            disabled={disabled || isFirstPage}
          >
            {"<"}
          </PageButton>

          {pageItems.map(item => {
            if (typeof item !== "number") {
              return <Ellipsis key={item}>...</Ellipsis>;
            }

            return (
              <PageButton
                key={item}
                type="button"
                isActive={item === safePage}
                onClick={() => onPageChange(item)}
                disabled={disabled || item === safePage}
                aria-label={`Go to page ${item}`}
                aria-current={item === safePage ? "page" : undefined}
              >
                {item}
              </PageButton>
            );
          })}

          <PageButton
            type="button"
            aria-label="Go to next page"
            onClick={() => onPageChange(safePage + 1)}
            disabled={disabled || isLastPage}
          >
            {">"}
          </PageButton>

          <PageButton
            type="button"
            aria-label="Go to last page"
            onClick={() => onPageChange(safeTotalPages)}
            disabled={disabled || isLastPage}
          >
            {">>"}
          </PageButton>
        </>
      )}

      <TotalFound>{safeTotal} found</TotalFound>
    </PaginationWrap>
  );
};

function getPageItems(page: number, totalPages: number): PageItem[] {
  if (totalPages <= 1) {
    return [1];
  }

  // Always include first/last and the current window around the active page.
  const basePages = [1, page - 1, page, page + 1, totalPages].filter(
    candidate => candidate >= 1 && candidate <= totalPages
  );

  const uniquePages = Array.from(new Set(basePages)).sort((a, b) => a - b);

  const items: PageItem[] = [];

  uniquePages.forEach((currentPage, index) => {
    if (index === 0) {
      items.push(currentPage);
      return;
    }

    const previousPage = uniquePages[index - 1];
    const gap = currentPage - previousPage;

    if (gap === 2) {
      items.push(previousPage + 1);
    } else if (gap > 2) {
      items.push(index === 1 ? "ellipsis-left" : "ellipsis-right");
    }

    items.push(currentPage);
  });

  return items;
}

export default Pagination;
